import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Landmark, Building2, Users, Search, AlertTriangle, RefreshCw } from 'lucide-react';
import { shortDate } from '@/lib/utils';
import {
  getSystemSubscriptionPayments,
  getSystemSubscriptionStats,
  type SystemSubscriptionPaymentRow,
} from '@/lib/workerApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DataPagination } from '@/components/DataPagination';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// The DB's own status enum — see brand_subscription_payments/
// account_subscription_payments. 'succeeded' is the only status webhooked on
// signup; 'refunded'/'disputed' land on the same row later via handleRefund/
// handleDisputeCreated, and 'failed' gets its own row per dunning attempt.
type StatusTab = 'all' | 'succeeded' | 'refunded' | 'failed' | 'disputed';
const STATUS_TABS: { key: StatusTab; label: string }[] = [
  { key: 'all',       label: 'All' },
  { key: 'succeeded', label: 'Succeeded' },
  { key: 'refunded',  label: 'Refunded' },
  { key: 'failed',    label: 'Failed' },
  { key: 'disputed',  label: 'Disputed' },
];

const TIER_LABEL: Record<string, string> = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
  pro_annual: 'Pro (Annual)',
  agency: 'Agency',
};

function usd(cents: number | null | undefined) {
  if (cents === null || cents === undefined) return null;
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === 'succeeded'
      ? 'bg-green-100 text-green-800 border-green-200'
      : status === 'failed'
        ? 'bg-red-100 text-red-800 border-red-200'
        : status === 'disputed'
          ? 'bg-orange-100 text-orange-800 border-orange-200'
          : status === 'refunded'
            ? 'bg-slate-100 text-slate-700 border-slate-200'
            : 'bg-gray-100 text-gray-700 border-gray-200';
  return <Badge className={`border ${cls} capitalize`}>{status}</Badge>;
}

function SourceBadge({ source }: { source: 'brand' | 'account' }) {
  return source === 'account'
    ? <Badge className="bg-purple-100 text-purple-800 border border-purple-200 text-[10px] px-1.5 py-0">Agency</Badge>
    : <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px] px-1.5 py-0">Brand</Badge>;
}

function TierBadge({ tier }: { tier: string | null }) {
  if (!tier) return <span className="text-xs text-muted-foreground">—</span>;
  const cls =
    tier === 'agency'
      ? 'bg-purple-100 text-purple-800 border-purple-200'
      : tier === 'pro' || tier === 'pro_annual'
        ? 'bg-blue-100 text-blue-800 border-blue-200'
        : 'bg-slate-100 text-slate-700 border-slate-200';
  return <Badge className={`border ${cls}`}>{TIER_LABEL[tier] ?? tier}</Badge>;
}

/**
 * Every row here is always tied to a brand or an account — this table is our
 * own ledger, keyed by a required foreign key, unlike the Whop tab where a
 * renewal payment can carry no metadata at all. Brand email comes straight off
 * brand_configs; agency email is the account owner's profiles.email (accounts
 * itself has no email column) — see the worker endpoint for both lookups.
 */
function PayerCell({ row }: { row: SystemSubscriptionPaymentRow }) {
  const name = row.account_name ?? row.brand_name;
  const email = row.account_email ?? row.brand_email;
  return (
    <div>
      <div className="font-medium truncate">{name ?? '—'}</div>
      {email && <div className="text-[11px] text-muted-foreground truncate">{email}</div>}
      {!name && !email && <div className="text-[11px] text-muted-foreground">name unresolved</div>}
    </div>
  );
}

type StatAccent = 'emerald' | 'sky' | 'purple';

// Mirrors WhopSubscriptionsPanel's STAT_ACCENTS/StatCard exactly, so the two
// "revenue totals" tabs read as the same design, not two different ones.
// Kept as a local duplicate rather than a shared import — see that file's
// comment for why (only these two panels need the sky/purple pair).
const STAT_ACCENTS: Record<StatAccent, { tile: string; value: string; glow: string; pill: string; bar: string }> = {
  emerald: {
    tile: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    value: 'text-emerald-600 dark:text-emerald-400',
    glow: 'from-emerald-500/[0.12]',
    pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
    bar: 'bg-emerald-500',
  },
  sky: {
    tile: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
    value: 'text-foreground',
    glow: 'from-sky-500/[0.12]',
    pill: 'bg-sky-100 text-sky-700 border border-sky-200',
    bar: 'bg-sky-500',
  },
  purple: {
    tile: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    value: 'text-foreground',
    glow: 'from-purple-500/[0.12]',
    pill: 'bg-purple-100 text-purple-700 border border-purple-200',
    bar: 'bg-purple-500',
  },
};

/**
 * Premium tile for the three revenue totals — same rounded-xl + corner-glow
 * treatment as WhopSubscriptionsPanel's StatCard (which itself matches
 * HybridLedger's LedgerStatCard). The succeeded-charge count is a colored
 * pill instead of plain caption text. No truncation state here — unlike the
 * Whop tab's live scan, this is a straight DB read over the full filtered
 * set, so the count is always exact.
 */
function StatCard({ icon: Icon, label, value, count, countLabel = 'succeeded', sub, accent, segments, loading }: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  /** Succeeded-charge count for the pill. Omitted while loading. */
  count?: number;
  countLabel?: string;
  sub?: string;
  accent: StatAccent;
  /** This card's share of the total, as filled segments (0-100 each) under
   *  the pill row — e.g. brand's cut in blue, agency's in purple. */
  segments?: { pct: number; className: string }[];
  loading?: boolean;
}) {
  const a = STAT_ACCENTS[accent];
  return (
    <div className="group relative overflow-hidden rounded-xl border bg-card p-5 shadow-sm transition-shadow duration-200 hover:shadow-elevate-lg">
      <div className={`pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${a.glow} to-transparent blur-2xl transition-opacity duration-200 group-hover:opacity-125`} />
      <div className="relative space-y-3">
        <div className="flex items-center gap-2">
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${a.tile}`}>
            <Icon className="h-4 w-4" />
          </span>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        </div>

        {loading ? (
          <Skeleton className="h-9 w-32" />
        ) : (
          <p className={`text-3xl font-bold leading-none tracking-tight tabular-nums ${a.value}`}>{value}</p>
        )}

        <div className="flex min-h-[22px] flex-wrap items-center gap-2">
          {loading ? (
            <Skeleton className="h-[22px] w-24 rounded-full" />
          ) : (
            <>
              {count !== undefined && (
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums ${a.pill}`}>
                  {count.toLocaleString()} {countLabel}
                </span>
              )}
              {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
            </>
          )}
        </div>

        {!loading && segments && segments.length > 0 && (
          <div className="flex h-1 w-full overflow-hidden rounded-full bg-muted">
            {segments.map((s, i) => (
              <div
                key={i}
                className={`h-full transition-all duration-500 ${s.className}`}
                style={{ width: `${Math.max(0, Math.min(s.pct, 100))}%` }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Subscription charges as OUR DB recorded them (brand_subscription_payments +
 * account_subscription_payments, written by the Whop webhook) — as opposed to
 * WhopSubscriptionsPanel, which reads live from Whop.
 *
 * This is a straight DB read, so unlike the Whop tab it CAN show real revenue
 * totals over the full filtered set (amount_cents here is already the
 * discount-aware settlement amount, captured at charge time). The tradeoff is
 * completeness, not accuracy: a payment the webhook never recorded (downtime,
 * an unhandled event) is invisible here even though it happened on Whop. Use
 * the Whop tab's "Reconcile against database" action to check for that gap.
 */
export default function SystemSubscriptionsPanel() {
  const [statusTab, setStatusTab]     = useState<StatusTab>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');
  const [dateFrom, setDateFrom]       = useState('');
  const [dateTo, setDateTo]           = useState('');
  const [page, setPage]               = useState(1);
  const [pageSize, setPageSize]       = useState(25);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Listing: paginated rows for the table. Excludes statusTab/page/pageSize
  // from the stats query below on purpose — see that query's comment.
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['system-subscription-payments', statusTab, search, dateFrom, dateTo, page, pageSize],
    queryFn: () => getSystemSubscriptionPayments({
      limit: pageSize,
      offset: (page - 1) * pageSize,
      statuses: statusTab === 'all' ? undefined : [statusTab],
      search: search.trim() || undefined,
      created_after: dateFrom ? new Date(dateFrom).toISOString() : undefined,
      created_before: dateTo
        ? new Date(new Date(dateTo).setHours(23, 59, 59, 999)).toISOString()
        : undefined,
    }),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Stats: revenue totals + every status's count at once, for the stat cards,
  // the breakdown panel, and the tab badges. A separate, parallel call from
  // the listing query above. Excludes statusTab/page/pageSize from its
  // queryKey/params: it always covers every status, so the tab badges never
  // collapse to whichever tab happens to be selected, and it's the same
  // regardless of which page is showing.
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['system-subscription-stats', search, dateFrom, dateTo],
    queryFn: () => getSystemSubscriptionStats({
      search: search.trim() || undefined,
      created_after: dateFrom ? new Date(dateFrom).toISOString() : undefined,
      created_before: dateTo
        ? new Date(new Date(dateTo).setHours(23, 59, 59, 999)).toISOString()
        : undefined,
    }),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const rows       = data?.rows ?? [];
  const totalItems = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const statusCounts = statsData?.status_counts ?? {};

  // Brand vs agency's share of the total succeeded amount, for the stat
  // cards' proportion bars. Zero (not NaN) while stats haven't loaded or
  // nothing's been collected yet.
  const totalCents  = statsData?.stats.total_succeeded_cents ?? 0;
  const brandCents  = statsData?.stats.by_source.brand.amount_cents ?? 0;
  const agencyCents = statsData?.stats.by_source.account.amount_cents ?? 0;
  const brandShare  = totalCents > 0 ? (brandCents / totalCents) * 100 : 0;
  const agencyShare = totalCents > 0 ? (agencyCents / totalCents) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Read from our own database (webhook-recorded), not live from Whop. Fast enough for real
          revenue totals, but only as complete as the webhook's own coverage.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          {isFetching ? 'Refreshing' : 'Refresh'}
        </Button>
      </div>

      {/* Mirrors the Whop tab's 3-card layout (Total Amount / Brand
          Subscriptions / Agency Subscriptions) — 'succeeded' charges only,
          same as that tab restricts to 'paid'. Refunded/failed totals aren't
          dropped, just relocated into the breakdown panel below so nothing is
          lost, they just aren't in the primary 3 boxes any more. The pill
          under each amount is that same 'succeeded'-only population (see
          handleAdminSystemSubscriptionStats — by_source's count used to be
          every row regardless of status, which mismatched the "Succeeded
          revenue by source" label it's shown under in the breakdown panel;
          fixed to count succeeded rows only, same as the amount). */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <StatCard
          icon={Landmark}
          label="Total Amount"
          value={statsLoading ? '—' : usd(totalCents)}
          count={statsData?.stats.total_succeeded_count}
          sub={statsLoading || totalCents === 0 ? undefined : `${brandShare.toFixed(0)}% brand · ${agencyShare.toFixed(0)}% agency`}
          accent="emerald"
          segments={[
            { pct: brandShare, className: 'bg-sky-500' },
            { pct: agencyShare, className: 'bg-purple-500' },
          ]}
          loading={statsLoading}
        />
        <StatCard
          icon={Building2}
          label="Brand Subscriptions"
          value={statsLoading ? '—' : usd(brandCents)}
          count={statsData?.stats.by_source.brand.count}
          sub="starter, pro, pro annual"
          accent="sky"
          segments={[{ pct: brandShare, className: 'bg-sky-500' }]}
          loading={statsLoading}
        />
        <StatCard
          icon={Users}
          label="Agency Subscriptions"
          value={statsLoading ? '—' : usd(agencyCents)}
          count={statsData?.stats.by_source.account.count}
          sub="agency plan"
          accent="purple"
          segments={[{ pct: agencyShare, className: 'bg-purple-500' }]}
          loading={statsLoading}
        />
      </div>

      {/* {statsData && (
        <details className="rounded-lg border bg-muted/30 px-4 py-3 text-sm">
          <summary className="cursor-pointer font-medium text-muted-foreground">
            Breakdown — {statsData.total_count} rows ({statsData.scanned.brand_rows} brand + {statsData.scanned.account_rows} agency scanned)
          </summary>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Succeeded revenue by tier</p>
              <ul className="text-xs font-mono space-y-0.5 mt-1">
                {Object.entries(statsData.stats.by_tier).map(([tier, s]) => (
                  <li key={tier}>{TIER_LABEL[tier] ?? tier}: <strong>{usd(s.amount_cents)}</strong> ({s.count})</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Succeeded revenue by source</p>
              <ul className="text-xs font-mono space-y-0.5 mt-1">
                <li>Brand: <strong>{usd(statsData.stats.by_source.brand.amount_cents)}</strong> ({statsData.stats.by_source.brand.count})</li>
                <li>Agency: <strong>{usd(statsData.stats.by_source.account.amount_cents)}</strong> ({statsData.stats.by_source.account.count})</li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Refunded / Failed</p>
              <ul className="text-xs font-mono space-y-0.5 mt-1">
                <li>Refunded: <strong className="text-red-600">{usd(statsData.stats.total_refunded_cents)}</strong> ({statsData.status_counts.refunded ?? 0})</li>
                <li>Failed (never collected): <strong>{usd(statsData.stats.total_failed_cents)}</strong> ({statsData.status_counts.failed ?? 0})</li>
              </ul>
            </div>
          </div>
        </details>
      )} */}

      <div className="rounded-lg border bg-card">
        <div className="flex flex-col gap-2 border-b px-4 pt-1 pb-3">
          <div className="flex items-center gap-1 flex-wrap">
            {STATUS_TABS.map(({ key, label }) => {
              const count = key === 'all'
                ? Object.values(statusCounts).reduce((s, n) => s + Number(n), 0)
                : Number(statusCounts[key] ?? 0);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setStatusTab(key); setPage(1); }}
                  className={`px-3 py-2.5 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    statusTab === key
                      ? 'border-b-2 border-foreground text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {label}
                  <span className={`text-xs rounded-full px-1.5 py-0.5 tabular-nums ${statusTab === key ? 'bg-foreground/10 text-foreground' : 'bg-muted text-muted-foreground'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search brand, account, email, plan, payment id..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="system-date-from" className="text-xs text-muted-foreground whitespace-nowrap">From</Label>
              <Input
                id="system-date-from"
                type="date"
                value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                className="h-8 text-sm"
              />
              <Label htmlFor="system-date-to" className="text-xs text-muted-foreground whitespace-nowrap">To</Label>
              <Input
                id="system-date-to"
                type="date"
                value={dateTo}
                onChange={e => { setDateTo(e.target.value); setPage(1); }}
                className="h-8 text-sm"
              />
            </div>
          </div>
        </div>

        {error ? (
          <div className="flex flex-col items-center justify-center py-16 text-sm gap-3 px-6 text-center">
            <AlertTriangle className="h-10 w-10 text-red-400" />
            <p className="font-medium text-red-700">Could not load subscription payments from the database.</p>
            <p className="text-muted-foreground max-w-md">{(error as Error).message}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>Try again</Button>
          </div>
        ) : isLoading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : totalItems === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground text-sm gap-2">
            <Landmark className="h-10 w-10 opacity-25 mb-2" />
            <p className="font-medium">No subscription payments found.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Brand / Account</TableHead>
                    <TableHead className="whitespace-nowrap">Source</TableHead>
                    <TableHead className="whitespace-nowrap">Plan</TableHead>
                    <TableHead className="whitespace-nowrap">Status</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Amount</TableHead>
                    <TableHead className="whitespace-nowrap">Promo</TableHead>
                    <TableHead className="whitespace-nowrap">Paid</TableHead>
                    <TableHead className="whitespace-nowrap">Membership ID</TableHead>
                    <TableHead className="whitespace-nowrap">Payment ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(row => (
                    <TableRow key={row.id}>
                      <TableCell className="max-w-[260px]"><PayerCell row={row} /></TableCell>
                      <TableCell><SourceBadge source={row.source} /></TableCell>
                      <TableCell><TierBadge tier={row.plan_tier} /></TableCell>
                      <TableCell><StatusBadge status={row.status} /></TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap font-medium">
                        {usd(row.amount_cents)}
                      </TableCell>
                      <TableCell>
                        {row.promo_code ? (
                          <Badge className="bg-indigo-100 text-indigo-800 border border-indigo-200 text-[10px] px-1.5 py-0">
                            {row.promo_code}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {shortDate(row.paid_at)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                        {row.whop_membership_id ?? '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground font-mono">
                        {row.whop_payment_id}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <DataPagination
              currentPage={page}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </>
        )}
      </div>
    </div>
  );
}
